import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CustomCodeInjector - Injects custom header and footer code from site settings
 * 
 * - Favicon: Updates the browser tab icon dynamically
 * - Header code: Injected into <head> element (for analytics, tracking pixels)
 * - Footer code: Injected before </body> (for chat widgets, social buttons)
 * 
 * This component should be rendered once at the app root level.
 */
const CustomCodeInjector = () => {
  const [injected, setInjected] = useState(false);

  useEffect(() => {
    // Only inject once
    if (injected) return;

    const injectCustomCode = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        const settings = response.data;

        // Update favicon dynamically
        if (settings.favicon_url) {
          // Remove existing favicon links
          const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
          existingFavicons.forEach(link => link.remove());
          
          // Add new favicon
          const faviconLink = document.createElement('link');
          faviconLink.rel = 'icon';
          faviconLink.type = 'image/png';
          faviconLink.href = settings.favicon_url;
          document.head.appendChild(faviconLink);
          
          // Also add apple-touch-icon for iOS
          const appleTouchIcon = document.createElement('link');
          appleTouchIcon.rel = 'apple-touch-icon';
          appleTouchIcon.href = settings.favicon_url;
          document.head.appendChild(appleTouchIcon);
        }

        // Inject header code into <head>
        if (settings.header_code) {
          const headerContainer = document.createElement('div');
          headerContainer.id = 'custom-header-code';
          headerContainer.innerHTML = settings.header_code;
          
          // Execute scripts by re-creating them (innerHTML doesn't execute scripts)
          const scripts = headerContainer.querySelectorAll('script');
          scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            
            // Copy all attributes
            Array.from(oldScript.attributes).forEach((attr) => {
              newScript.setAttribute(attr.name, attr.value);
            });
            
            // Copy inline content
            if (oldScript.innerHTML) {
              newScript.innerHTML = oldScript.innerHTML;
            }
            
            // Replace old script with new one (to execute it)
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
          
          // Move all child nodes to head
          const fragment = document.createDocumentFragment();
          while (headerContainer.firstChild) {
            fragment.appendChild(headerContainer.firstChild);
          }
          document.head.appendChild(fragment);
        }

        // Inject footer code before </body>
        if (settings.footer_code) {
          const footerContainer = document.createElement('div');
          footerContainer.id = 'custom-footer-code';
          footerContainer.innerHTML = settings.footer_code;
          
          // Execute scripts by re-creating them (innerHTML doesn't execute scripts)
          const scripts = footerContainer.querySelectorAll('script');
          scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            
            // Copy all attributes
            Array.from(oldScript.attributes).forEach((attr) => {
              newScript.setAttribute(attr.name, attr.value);
            });
            
            // Copy inline content
            if (oldScript.innerHTML) {
              newScript.innerHTML = oldScript.innerHTML;
            }
            
            // Replace old script with new one (to execute it)
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
          
          document.body.appendChild(footerContainer);
        }

        setInjected(true);
      } catch (error) {
        // Silently fail - settings might not be available
        console.error('Failed to load custom code settings:', error);
      }
    };

    injectCustomCode();
  }, [injected]);

  // This component doesn't render anything
  return null;
};

export default CustomCodeInjector;
